import { INotice, Notifier } from "@airbrake/browser";
import _ from "lodash";
import * as React from "react";
import { Params } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { ProblemDetailError } from "~/lib/error";
import { User } from "~/models";
import { Session } from "./session";

export interface ErrorContext {
  session?: Session | null;
  params?: Readonly<Params<string>>;
  environment?: {
    request_id?: string | null;
  };
}

export interface AirbrakeContextValue {
  airbrake: Notifier;
  notify: (error: Error) => Promise<INotice>;
  errorContext: ErrorContext | null;
  setErrorContext: React.Dispatch<React.SetStateAction<ErrorContext | null>>;
}

export const AirbrakeContext = React.createContext<AirbrakeContextValue | null>(
  null,
);

export const useAirbrake = () => {
  const contextValue = React.useContext<AirbrakeContextValue | null>(
    AirbrakeContext,
  );
  if (!contextValue)
    throw new Error("useAirbrake must be used within an AirbrakeProvider");

  return contextValue;
};

export const AirbrakeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [errorContext, setErrorContext] = React.useState<ErrorContext | null>(
    null,
  );

  const airbrake: Notifier = React.useMemo(
    () =>
      new Notifier({
        projectId: 1,
        projectKey: "01a2ca5a82004d31af1fa56e549fccb7",
        host: "https://errbit.thefloow.net",
        instrumentation: {
          onerror: false,
        },
        errorNotifications: true,
        keysBlocklist: [
          "password",
          "reset_code",
          "new_password",
          "new_password_confirmation",
          "ems_credentials",
          "tct_credentials",
          "unique_secret_hash",
          "token",
        ],
      }),
    [],
  );

  const environment = document.head
    .querySelector('meta[name="environment"]')
    ?.getAttribute("content");
  const version = document.head
    .querySelector('meta[name="version"]')
    ?.getAttribute("content");

  airbrake.addFilter((notice) => {
    notice.context.environment = environment;
    notice.context.version = version;
    return notice;
  });

  airbrake.addFilter((notice) => {
    if (["development", "test"].includes(notice.context.environment))
      return null;
    return notice;
  });

  const parseUser = (user: User | undefined) => {
    if (!user) return;
    let parsedUser = _.omit(user, ["enabled"]);
    // parse for Errbit < 0.10.0 which doesn't support JSON
    parsedUser.roles = JSON.stringify(_.get(parsedUser, "roles"));
    parsedUser.permissions = JSON.stringify(_.get(parsedUser, "permissions"));
    return parsedUser;
  };

  React.useEffect(() => {
    if (errorContext)
      airbrake.addFilter((notice) => {
        const parsedUser = parseUser(errorContext?.session?.user);
        return (notice = {
          ...notice,
          context: {
            ...notice.context,
            ...(parsedUser && { user: parsedUser }),
          },
          params: {
            ...notice.params,
            ...errorContext?.params,
          },
        });
      });
  }, [errorContext]);

  const notify = async (error: Error): Promise<INotice> => {
    const request_id =
      error instanceof ProblemDetailError
        ? error?.problem?.id || uuidv4()
        : uuidv4();
    const notice = await airbrake.notify({
      error: error,
      environment: {
        ...(request_id && { request_id }),
      },
    });
    return notice;
  };

  const defaultContextValue: AirbrakeContextValue = React.useMemo(
    () => ({
      airbrake: airbrake,
      notify: notify,
      errorContext: errorContext,
      setErrorContext: setErrorContext,
    }),
    [errorContext],
  );

  return (
    <AirbrakeContext.Provider value={defaultContextValue}>
      {children}
    </AirbrakeContext.Provider>
  );
};
